import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Button from '../../components/buttons/Button';
import { FaSave, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import { SPECIALTIES } from '../../constants/specialties';
import FullScreenTextEditor from '../../components/editors/FullScreenTextEditor';
import { useFullScreenTextarea } from '../../hooks/useFullScreenTextEditor';
import Statistics from '../../Home/Statistics';
import { statisticService } from '../../services';
import { Statistic } from '../../models';

const GeneralTab = ({
  enterpriseDetails,
  licenseUsage,
  isEditingEnterprise,
  editedEnterprise,
  editingLoading,
  showCustomSpecialty,
  customSpecialty,
  enterpriseDescriptionEditor,
  startEditEnterprise,
  cancelEditEnterprise,
  handleSpecializationChange,
  handleCustomSpecialtyChange,
  handleCustomSpecialtyBlur,
  saveEnterpriseDetails,
  setEditedEnterprise,
  onDeleteEnterprise
}) => {
  const [enterpriseStats, setEnterpriseStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        // Align with Home.js logic and pass selected enterpriseId
        const raw = await statisticService.getEnterpriseStatistics('global', enterpriseDetails?.id);
        const statsModel = new Statistic({
          statistics: raw?.statistics || raw,
          profileCreatedAt: enterpriseDetails?.createdAt
        });
        if (isMounted) setEnterpriseStats(statsModel);
      } catch (e) {
        if (isMounted) setEnterpriseStats(null);
      } finally {
        if (isMounted) setStatsLoading(false);
      }
    };

    if (enterpriseDetails?.id) {
      fetchStats();
    }
    return () => { isMounted = false; };
  }, [enterpriseDetails?.id]);
  return (
    <div className="enterprise-admin-general-tab">
      <h2>Informations générales</h2>
      
      {/* License Usage */}
      {enterpriseDetails && enterpriseDetails.licensingMode !== 'none' && (
        <div className="enterprise-admin-license-usage">
          <h3>Utilisation de la licence</h3>
          {licenseUsage ? (
            <div className="enterprise-admin-license-stats">
              <div className="enterprise-admin-license-stat">
                <span className="enterprise-admin-label">Licences:</span>
                <span className="enterprise-admin-value">{licenseUsage.currentLicenses} / {licenseUsage.maxLicenses}</span>
              </div>
              <div className="enterprise-admin-license-bar">
                <div 
                  className="enterprise-admin-license-usage-fill" 
                  style={{ width: `${(parseInt(licenseUsage.currentLicenses) / licenseUsage.maxLicenses) * 100}%` }}
                ></div>
              </div>
              <span className="enterprise-admin-usage-percentage">
                {Math.round((parseInt(licenseUsage.currentLicenses) / licenseUsage.maxLicenses) * 100)}% d'utilisation
              </span>
            </div>
          ) : (
            <p>Chargement des informations de licence...</p>
          )}
        </div>
      )}

      {/* Enterprise Information */}
      <div className="enterprise-admin-info">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Informations de l'organisation</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!isEditingEnterprise && enterpriseDetails && (
              <Button 
                onClick={startEditEnterprise}
                icon={FaEdit}
                variant="info"
                style={{ marginBottom: 0 }}
              >
                Éditer
              </Button>
            )}
            {!isEditingEnterprise && enterpriseDetails && enterpriseDetails.licensingMode === 'none' && (
              <Button 
                onClick={onDeleteEnterprise}
                icon={FaTrash}
                variant="danger"
                style={{ marginBottom: 0 }}
              >
                Supprimer l'organisation
              </Button>
            )}
          </div>
        </div>
        
                  {isEditingEnterprise ? (
            <div className="enterprise-admin-edit-form">
              <div className="enterprise-admin-form-group">
                <label>Description:</label>
                <div className="enterprise-admin-textarea-container">
                  <textarea
                    value={editedEnterprise.description || ''}
                    onChange={(e) => setEditedEnterprise({...editedEnterprise, description: e.target.value})}
                    className="enterprise-admin-textarea"
                    placeholder="Entrez la description de l'organisation..."
                    rows={4}
                    onFocus={() => {
                      if (enterpriseDescriptionEditor && enterpriseDescriptionEditor.open) {
                        enterpriseDescriptionEditor.open(editedEnterprise.description || '');
                      }
                    }}
                  />
                </div>
              </div>
              <div className="enterprise-admin-form-actions">
                <Button 
                  onClick={saveEnterpriseDetails} 
                  disabled={editingLoading}
                  icon={FaSave}
                >
                  {editingLoading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
                <Button
                  type="inversed"
                  onClick={cancelEditEnterprise} 
                  disabled={editingLoading}
                  icon={FaTimes}
                >
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
          enterpriseDetails && (
            <div className="enterprise-admin-info-grid">
              <div className="enterprise-admin-info-item">
                <span className="enterprise-admin-label">Nom:</span>
                <span className="enterprise-admin-value">{enterpriseDetails.name}</span>
              </div>
              <div className="enterprise-admin-info-item">
                <span className="enterprise-admin-label">Nom court:</span>
                <span className="enterprise-admin-value">{enterpriseDetails.shortName || '-'}</span>
              </div>
              <div className="enterprise-admin-info-item full-width">
                <span className="enterprise-admin-label">Description:</span>
                <span className="enterprise-admin-value">{enterpriseDetails.description || '-'}</span>
              </div>
              <div className="enterprise-admin-info-item">
                <span className="enterprise-admin-label">Spécialité:</span>
                <span className="enterprise-admin-value">{enterpriseDetails.specialty || '-'}</span>
              </div>
              <div className="enterprise-admin-info-item">
                <span className="enterprise-admin-label">Statut:</span>
                <span className={`enterprise-admin-value ${enterpriseDetails.isActive ? 'status-active' : 'status-inactive'}`}>
                  {enterpriseDetails.isActive ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <div className="enterprise-admin-info-item">
                <span className="enterprise-admin-label">Date de création:</span>
                <span className="enterprise-admin-value">
                  {enterpriseDetails.createdAt ? format(new Date(enterpriseDetails.createdAt), 'dd/MM/yyyy HH:mm') : '-'}
                </span>
              </div>
              <div className="enterprise-admin-info-item">
                <span className="enterprise-admin-label">Dernière mise à jour:</span>
                <span className="enterprise-admin-value">
                  {enterpriseDetails.updatedAt ? format(new Date(enterpriseDetails.updatedAt), 'dd/MM/yyyy HH:mm') : '-'}
                </span>
              </div>
              <div
                className="enterprise-admin-info-item full-width"
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <Statistics statistics={enterpriseStats} period="global" loading={statsLoading} context="enterprise" />
              </div>
            </div>
          )
        )}
      </div>

      {/* Fullscreen Editor */}
      {enterpriseDescriptionEditor && <FullScreenTextEditor {...enterpriseDescriptionEditor.editorProps} />}
    </div>
  );
};

export default GeneralTab; 